// ── MÅLKORT ───────────────────────────────────────────────────────
// Budgeten exponerad där personalen faktiskt tittar. Blocken visade förut
// bara utfall och ifjol — frågan blev "vad blev det" i stället för "når vi
// målet". Kortet hämtar sitt eget underlag, så det räcker att veta vilken
// Qlik-resultatenhet blocket handlar om.
//
// Vald budgetversion: fastställd om det finns en, annars B0. Saknas budget
// helt renderas ingenting — inget tomt kort som skräpar.
import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getDailyUnits, getLanthandel } from '../qlik';
import { raStartYear } from '../utils';

const MAN_NAMN = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli',
  'augusti', 'september', 'oktober', 'november', 'december'];

// Resultatenhet → vilket bolags budget och vilken rad den hör till.
// Lanthandeln saknar Qlik-koppling: utfallet kommer ur HonestBox och är
// INKL moms, medan budgeten är ex moms — därför egen källa och omräkning.
const ENHET_RAD = {
  'Hotell Logi':        ['hotell', 'intakt:hotell'],
  'Hotell Förmedling':  ['hotell', 'intakt:formedling'],
  'Hotell Aktivitet':   ['hotell', 'intakt:aktivitet'],
  'Hotellrestaurangen': ['hotell', 'intakt:restaurang'],
  'Brasserie Kerstin':  ['hotell', 'intakt:brasserie'],
  'Knallen':            ['hotell', 'intakt:knallen'],
  'Vävstugan':          ['hotell', 'intakt:vavstugan'],
  'Stugservice':        ['hotell', 'intakt:stugservice'],
  'Sportcenter':        ['sc',     'intakt:sportcenter'],
  'Lanthandeln':        ['stigmyhrs', 'intakt:lanthandel', 'honestbox'],
};

// Hela koncernens intäktsbärande enheter — för Ledningsblocket
export const ALLA_ENHETER = Object.keys(ENHET_RAD);

const LS_NYCKEL = 'malkort-oppen';

export default function MalKort({ enheter, rubrik = 'Månadens mål' }) {
  const lista = useMemo(() => (Array.isArray(enheter) ? enheter : [enheter]).filter(Boolean), [enheter]);
  const [data, setData] = useState(null);
  // Hopfällt som standard — kortet ska inte kosta plats innan man bett om det.
  // Valet sparas så det stannar som användaren lämnade det.
  const [oppen, setOppen] = useState(() => {
    try { return localStorage.getItem(LS_NYCKEL) === '1'; } catch { return false; }
  });
  const vaxla = () => setOppen((o) => {
    try { localStorage.setItem(LS_NYCKEL, o ? '0' : '1'); } catch { /* privat läge */ }
    return !o;
  });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const bolagSet = [...new Set(lista.map((e) => ENHET_RAD[e]?.[0]).filter(Boolean))];
        if (!bolagSet.length) return;
        const behoverLh = lista.some((e) => ENHET_RAD[e]?.[2] === 'honestbox');
        const y = raStartYear();
        const ra = `${String(y % 100).padStart(2, '0')}${String((y + 1) % 100).padStart(2, '0')}`;
        const [units, lh, ...snaps] = await Promise.all([
          getDailyUnits(),
          behoverLh ? getLanthandel().catch(() => null) : Promise.resolve(null),
          ...bolagSet.map((b) => getDocs(query(collection(db, 'intel_budget'), where('bolag', '==', b), where('ra', '==', ra)))),
        ]);
        if (!live) return;

        // Budget per bolag: fastställd version vinner, annars B0
        const budget = {};
        bolagSet.forEach((b, i) => {
          const docs = snaps[i].docs.map((d) => d.data());
          budget[b] = docs.find((d) => d.status === 'faststalld') || docs.find((d) => d.version === 'B0') || docs[0];
        });

        const nu = new Date();
        const ym = `${nu.getFullYear()}${String(nu.getMonth() + 1).padStart(2, '0')}`;
        const iss = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const igarStr = iss(new Date(nu.getFullYear(), nu.getMonth(), nu.getDate() - 1));
        const pre = `${ym.slice(0, 4)}-${ym.slice(4)}`;
        // HonestBox levererar brutto — räkna om med den blandade satsen
        const lhFaktor = 1 + (lh?.moms?.sats || 0) / 100;

        const perEnhet = lista.map((enhet) => {
          const par = ENHET_RAD[enhet];
          if (!par) return null;
          const [b, radId, kalla] = par;
          const mal = ((budget[b]?.rader || {})[radId] || {})[ym] || 0;
          let utfall = 0, igar = 0;
          if (kalla === 'honestbox') {
            for (const [datum, oms] of (lh?.days || [])) {
              if (datum === igarStr) igar += (oms || 0) / lhFaktor;
              if (datum.startsWith(pre)) utfall += (oms || 0) / lhFaktor;
            }
          } else {
            for (const u of units) {
              if (u.enhet !== enhet) continue;
              if (u.datum === igarStr) igar += u.intakt || 0;
              if (u.datum.startsWith(pre)) utfall += u.intakt || 0;
            }
          }
          return { enhet, mal, utfall, igar };
        }).filter(Boolean);

        const mal = perEnhet.reduce((s, e) => s + e.mal, 0);
        const utfall = perEnhet.reduce((s, e) => s + e.utfall, 0);
        const igar = perEnhet.reduce((s, e) => s + e.igar, 0);
        // Hur stor del av månaden som gått — pace inom månaden
        const dagar = new Date(nu.getFullYear(), nu.getMonth() + 1, 0).getDate();
        setData({ mal, utfall, igar, dagar, kvarDagar: Math.max(dagar - nu.getDate(), 0),
          andelAvMan: nu.getDate() / dagar, manad: MAN_NAMN[nu.getMonth()], perEnhet });
      } catch { /* budgeten är en bonus i blocken — fela tyst */ }
    })();
    return () => { live = false; };
  }, [lista]);

  if (!data || !data.mal) return null;
  const { mal, utfall, igar, dagar, kvarDagar, andelAvMan, manad, perEnhet } = data;
  const andel = utfall / mal;
  const takt = andelAvMan > 0 ? andel / andelAvMan : 0;   // 1,0 = precis i takt
  const prognos = Math.round(mal * takt);
  const farg = takt >= 1 ? '#1D6A5F' : takt >= 0.9 ? '#854F0B' : '#A32D2D';
  const kr = (v) => Math.round(v).toLocaleString('sv-SE');

  const pe = Math.round((andel - andelAvMan) * 100);          // procentenheter före/efter
  const dagsmal = mal / dagar;
  const kvarKr = Math.max(mal - utfall, 0);
  const stapel = (
    <div style={{ position: 'relative', height: 8, background: 'var(--panel2)', borderRadius: 4, flex: 1, minWidth: 60 }}>
      <div style={{ height: '100%', width: `${Math.min(andel * 100, 100)}%`, background: farg, borderRadius: 4, transition: 'width .3s' }} />
      <div style={{ position: 'absolute', top: -3, bottom: -3, left: `${Math.min(andelAvMan * 100, 100)}%`, width: 2, background: 'var(--text)', opacity: 0.45 }}
        title={`Här borde ni ligga idag — ${Math.round(andelAvMan * 100)} % av ${manad} har gått`} />
    </div>
  );

  return (
    <div className="card" style={{ borderLeft: `3px solid ${farg}`, paddingTop: oppen ? undefined : 12, paddingBottom: oppen ? undefined : 12 }}>
      {/* Hopfällt läge: en rad som ändå svarar på frågan ligger vi före eller efter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif' }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: farg, lineHeight: 1, whiteSpace: 'nowrap' }}>
          {Math.round(andel * 100)} %
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          av {manad}
        </span>
        {stapel}
        <span style={{ fontSize: 12.5, fontWeight: 700, color: farg, whiteSpace: 'nowrap' }}>
          {pe > 0 ? `+${pe} pe före` : pe < 0 ? `${-pe} pe efter` : 'i takt'}
        </span>
        <button onClick={vaxla} aria-expanded={oppen} aria-label={oppen ? 'Dölj detaljer' : 'Visa detaljer'}
          style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>
          {oppen ? '▴' : '▾'}
        </button>
      </div>

      {oppen && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', fontFamily: 'system-ui,sans-serif' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{rubrik} — {manad}</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{kr(utfall)} av {kr(mal)} kr</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Håller takten →</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: farg }}>{kr(prognos)} kr</div>
              <div style={{ fontSize: 11, color: farg }}>{prognos >= mal ? '+' : ''}{kr(prognos - mal)} mot målet</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 12, fontFamily: 'system-ui,sans-serif' }}>
            {[
              ['Igår', kr(igar) + ' kr', igar >= dagsmal ? `+${kr(igar - dagsmal)} mot dagsmål` : `${kr(igar - dagsmal)} mot dagsmål`, igar >= dagsmal ? '#1D6A5F' : '#A32D2D'],
              ['Dagsmål', kr(dagsmal) + ' kr', `${kr(mal)} ÷ ${dagar} dagar`, 'var(--text-secondary)'],
              ['Kvar i månaden', kr(kvarKr) + ' kr', kvarDagar > 0 ? `${kr(kvarKr / kvarDagar)} kr/dag` : 'sista dagen', 'var(--text-secondary)'],
            ].map(([l, v, sub, c]) => (
              <div key={l}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{l}</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{v}</div>
                <div style={{ fontSize: 11, color: c }}>{sub}</div>
              </div>
            ))}
          </div>
          {/* Uppdelning per avdelning — bara meningsfull när kortet slår ihop flera */}
          {perEnhet.length > 1 && (
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              {[...perEnhet].filter((e) => e.mal || e.utfall)
                .sort((a, b) => (b.mal ? b.utfall / b.mal : 0) - (a.mal ? a.utfall / a.mal : 0))
                .map((e) => {
                  const a = e.mal > 0 ? e.utfall / e.mal : 0;
                  const c = e.mal <= 0 ? 'var(--text-secondary)' : a >= andelAvMan ? '#1D6A5F' : a >= andelAvMan * 0.9 ? '#854F0B' : '#A32D2D';
                  return (
                    <div key={e.enhet} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5, fontFamily: 'system-ui,sans-serif' }}>
                      <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', width: 150, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.enhet}</span>
                      <div style={{ position: 'relative', height: 5, background: 'var(--panel2)', borderRadius: 3, flex: 1, minWidth: 50 }}>
                        <div style={{ height: '100%', width: `${Math.min(a * 100, 100)}%`, background: c, borderRadius: 3 }} />
                        <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${Math.min(andelAvMan * 100, 100)}%`, width: 1.5, background: 'var(--text)', opacity: 0.35 }} />
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: c, width: 42, textAlign: 'right', flexShrink: 0 }}>
                        {e.mal > 0 ? `${Math.round(a * 100)} %` : '–'}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', width: 80, textAlign: 'right', flexShrink: 0 }}>
                        {kr(e.utfall)}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
          <div className="note" style={{ marginTop: 8 }}>
            Strecket i stapeln visar var ni borde ligga idag — {Math.round(andelAvMan * 100)} % av {manad} har gått.
            Dagsmålet är månadsbudgeten delad på antal dagar och är alltså platt; en lördag och en tisdag
            väger lika mycket här även om de sällan gör det i verkligheten.
          </div>
        </>
      )}
    </div>
  );
}
